'use client';

import {
  ACTIVITY_TREND_RANGES,
  ActivityTrendRangeSchema,
  type ActivityTrendRange,
} from '@terra/shared-contracts-applications';
import { LineChart, formatLineChartTooltip } from '@terra/ui-charts';
import { Filter, useThemeMode } from '@terra/ui-design-system';
import { HStack, token } from '@terra/ui-styled-system';
import { useFormatter, useTranslations } from 'next-intl';
import { useState } from 'react';
import { useActivityTrend } from '../../hooks/dashboard/useActivityTrend';
import {
  ACTIVITY_TREND_SERIES_COLOR_TOKENS,
  buildActivityTrendSeries,
  hasActivityTrendData,
  toActivityTrendBucketDate,
} from './activity-trend-series';
import { DashboardCard } from './DashboardCard';

const CHART_HEIGHT = 240;
const DEFAULT_RANGE: ActivityTrendRange = '30d';

type ActivityTrendCardProps = {
  applicationId: string;
};

export function ActivityTrendCard({ applicationId }: ActivityTrendCardProps) {
  const t = useTranslations('home.activityTrend');
  const format = useFormatter();
  const { mode: themeMode } = useThemeMode();
  const [range, setRange] = useState<ActivityTrendRange>(DEFAULT_RANGE);
  const { data, isLoading } = useActivityTrend({ applicationId, range });
  const buckets = data?.buckets ?? [];
  const series = buildActivityTrendSeries(buckets);
  const categories = buckets.map((bucket) =>
    format.dateTime(toActivityTrendBucketDate(bucket.start), {
      month: 'short',
      day: '2-digit',
      timeZone: 'UTC',
    }),
  );

  return (
    <DashboardCard
      title={t('title')}
      subtitle={t(`ranges.${range}`)}
      tooltip={t(
        data?.granularity === 'week' ? 'tooltipWeekly' : 'tooltipDaily',
      )}
      trailingContent={
        <HStack flexGrow={1} justifyContent="flex-end">
          <Filter
            id="activity-trend-range"
            required
            placeholder={t('rangePlaceholder')}
            items={ACTIVITY_TREND_RANGES.map((value) => ({
              value,
              label: t(`ranges.${value}`),
            }))}
            value={[range]}
            onChange={([value]) => {
              const parsed = ActivityTrendRangeSchema.safeParse(value);

              if (parsed.success) {
                setRange(parsed.data);
              }
            }}
          />
        </HStack>
      }
      isLoading={isLoading}
      isEmpty={!isLoading && !hasActivityTrendData(series)}
      emptyLabel={t('empty')}
      contentHeight={CHART_HEIGHT}
    >
      <LineChart
        series={series.map((entry) => ({
          name: t(`series.${entry.key}`),
          data: entry.data,
        }))}
        categories={categories}
        colors={series.map((entry) =>
          token(ACTIVITY_TREND_SERIES_COLOR_TOKENS[entry.key]),
        )}
        height={CHART_HEIGHT}
        curve="straight"
        strokeDashArray={0}
        showXAxisGridLines={false}
        showXAxisTooltip={false}
        rotateXAxisLabels={0}
        yaxisMin={0}
        yaxisTitle={t('yAxisTitle')}
        axisTitleStyle={{
          color: token('colors.fg.neutral.subtle'),
          fontSize: '12px',
        }}
        markers={{ size: 4, strokeWidth: 0, offsetX: -4 }}
        tooltipCustom={({ series: values, dataPointIndex, w }) =>
          formatLineChartTooltip({
            theme: themeMode ?? 'light',
            series: values,
            dataPointIndex,
            w,
            categories,
          })
        }
        showLegendForSingleSeries
        legendOffsetY={20}
        legendItemMargin={{ horizontal: 20 }}
        allowMouseWheelZoom={false}
      />
    </DashboardCard>
  );
}
